import React, { useEffect, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";

import { useUserStore } from "base/stores";
import { dateTimeFormatString } from "base/utils";

export const TIME_FORMAT_TYPES = {
  ABSOLUTE: "absolute",
  RELATIVE_LONG: "relative_long",
  RELATIVE_SHORT: "relative_short"
};

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

const LOCALE_OBJECT = {
  name: "short-type",
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "%ds",
    ss: "%ds",
    m: "1m",
    mm: "%dm",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    w: "1w",
    ww: "%dw",
    M: "1mo",
    MM: "%dmo",
    y: "1y",
    yy: "%dy"
  }
};

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "a few seconds",
    ss: "%d seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    dd: "%d days",
    w: "a week",
    ww: "%d weeks",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years"
  }
});

/**
 * Time component is used to display the give date and time in relative value
 */
export function Time(props) {
  const { date, enabled = true, type = TIME_FORMAT_TYPES.ABSOLUTE } = props;
  const [formattedDate, setFormattedDate] = useState(null);

  useEffect(
    function renderTime() {
      let timeoutHandler = null;
      const { currentUser } = useUserStore.getState();
      let userTimezone =
        (currentUser && currentUser.TimeZone._id) ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      const inputDaysJsObj = dayjs(date).tz(userTimezone);
      if (type === TIME_FORMAT_TYPES.ABSOLUTE) {
        setFormattedDate(formatTime(inputDaysJsObj, type, userTimezone));
      } else {
        const format = () => {
          setFormattedDate(formatTime(inputDaysJsObj, type, userTimezone));
          clearTimeout(timeoutHandler);
          if (enabled) {
            timeoutHandler = setTimeout(() => {
              window.requestAnimationFrame(format);
            }, 30000);
          }
        };
        window.requestAnimationFrame(format);
      }

      return () => {
        clearTimeout(timeoutHandler);
      };
    },
    [enabled, date, type]
  );
  return <React.Fragment>{formattedDate}</React.Fragment>;
}

Time.defaultProps = {
  enabled: true,
  type: TIME_FORMAT_TYPES.ABSOLUTE
};

Time.propTypes = {
  /**
   * Date object for which the from now will be displayed
   */
  date: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(dayjs)
  ]).isRequired,
  /**
   * Whether to auto update the from now every 30 seconds
   */
  enabled: PropTypes.bool,
  /**
   * Different type of relative we can use based on this type prop
   */
  type: PropTypes.oneOf(Object.values(TIME_FORMAT_TYPES))
};

export function formatTime(inputDaysJsObj, type, userTimezone) {
  // eslint-disable-next-line max-len
  const currentDateTimeOfUserZone = dayjs.tz(new Date(), userTimezone); // current date time of user timezone for relative time calculation
  const { currentUser } = useUserStore.getState();
  const currentLocale = (currentUser && currentUser.DateFormat) || "";
  let dateTimeFormat = dateTimeFormatString(currentLocale);

  if (
    type === TIME_FORMAT_TYPES.ABSOLUTE ||
    Math.abs(inputDaysJsObj.diff(currentDateTimeOfUserZone, "days")) > 365
  ) {
    return inputDaysJsObj.format(dateTimeFormat);
  } else if (
    Math.abs(inputDaysJsObj.diff(currentDateTimeOfUserZone, "seconds")) <= 10
  ) {
    return "now";
  } else if (type === TIME_FORMAT_TYPES.RELATIVE_SHORT) {
    return inputDaysJsObj.locale("short-type", LOCALE_OBJECT).fromNow();
  } else {
    return inputDaysJsObj.locale("en").fromNow();
  }
}
